import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';

function analyzeTemplate(template) {
  if (!template) return { bodyParams: [], headerType: null, hasNamedParams: false, bodyText: '' };
  let components = [];
  try { components = JSON.parse(template.components || '[]'); } catch {}

  const body = components.find(c => c.type === 'BODY');
  const header = components.find(c => c.type === 'HEADER');

  const namedMatches = (body?.text || '').match(/\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g) || [];
  const positionalMatches = (body?.text || '').match(/\{\{(\d+)\}\}/g) || [];
  const hasNamedParams = namedMatches.length > 0;

  const bodyParams = hasNamedParams
    ? namedMatches.map(m => ({ key: m.replace(/\{\{|\}\}/g, ''), value: '' }))
    : positionalMatches.map((m, i) => ({ key: String(i + 1), value: '' }));

  return {
    bodyParams,
    headerType: header?.format || null,
    hasNamedParams,
    bodyText: body?.text || '',
  };
}

function WindowBanner({ status, loading }) {
  if (loading) return (
    <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--text3)', border: '1px solid var(--border)' }}>
      ⏳ Checking conversation window...
    </div>
  );
  if (!status) return null;

  if (!status.lastInboundAt) return (
    <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--text3)', border: '1px solid var(--border)' }}>
      ⚫ No previous messages — use a template to start this conversation.
    </div>
  );

  if (!status.windowOpen) return (
    <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>⚠️</span>
      <span><strong>Window closed.</strong> Free-form text won't work — you must use a template.</span>
    </div>
  );

  const h = Math.floor(status.hoursRemaining);
  const m = status.minutesRemaining % 60;
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return (
    <div style={{ padding: '8px 12px', background: 'rgba(37,211,102,0.08)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--accent)', border: '1px solid rgba(37,211,102,0.2)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>✅</span>
      <span><strong>Window open</strong> — {label} remaining. Free-form text works.</span>
    </div>
  );
}

export default function Send() {
  const { activeNumber } = useNumbers();
  const [mode, setMode]               = useState('text');
  const [to, setTo]                   = useState('');
  const [text, setText]               = useState('');
  const [templates, setTemplates]     = useState([]);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [langCode, setLangCode]       = useState('en_US');
  const [bodyParams, setBodyParams]   = useState([]);
  const [headerUrl, setHeaderUrl]     = useState('');
  const [uploading, setUploading]     = useState(false);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');
  const [sending, setSending]         = useState(false);

  // Window status
  const [windowStatus, setWindowStatus] = useState(null);
  const [windowLoading, setWindowLoading] = useState(false);
  const windowDebounceRef = useRef(null);

  const fileRef = useRef();

  // Pre-fill from URL params (?to=...&mode=template)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const toParam = params.get('to');
    const modeParam = params.get('mode');
    if (toParam) setTo(toParam);
    if (modeParam === 'template' || modeParam === 'text') setMode(modeParam);
  }, []);

  useEffect(() => {
    if (!activeNumber) return;
    api.get(`/templates?wa_number_id=${activeNumber.id}`).then(r => setTemplates(r.data)).catch(() => {});
  }, [activeNumber]);

  // Debounced window check when 'to' changes
  const checkWindow = useCallback(async (number) => {
    if (!number || number.length < 7) { setWindowStatus(null); return; }
    setWindowLoading(true);
    try {
      const waNumId = activeNumber?.id;
      const res = await api.get(`/messages/window-status/${number}${waNumId ? '?wa_number_id=' + waNumId : ''}`);
      setWindowStatus(res.data);
    } catch {
      setWindowStatus(null);
    }
    setWindowLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(windowDebounceRef.current);
    if (!to) { setWindowStatus(null); setWindowLoading(false); return; }
    windowDebounceRef.current = setTimeout(() => checkWindow(to), 600);
    return () => clearTimeout(windowDebounceRef.current);
  }, [to, checkWindow]);

  // Auto-switch to template mode when window is closed
  useEffect(() => {
    if (windowStatus && !windowStatus.windowOpen && windowStatus.lastInboundAt !== null) {
      setMode('template');
    }
  }, [windowStatus]);

  const handleTemplateChange = (val) => {
    if (!val) { setSelectedTpl(null); setBodyParams([]); setHeaderUrl(''); return; }
    const [tName, tLang] = val.split('|');
    const tObj = templates.find(t => t.name === tName && t.language === tLang);
    setSelectedTpl(tObj || null);
    setLangCode(tLang || 'en_US');
    setHeaderUrl('');
    if (tObj) {
      const { bodyParams: bp } = analyzeTemplate(tObj);
      setBodyParams(bp);
    }
  };

  const updateParam = (idx, val) => {
    setBodyParams(prev => prev.map((p, i) => i === idx ? { ...p, value: val } : p));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/uploads', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHeaderUrl(res.data.url);
    } catch (err) {
      setError('File upload failed: ' + (err.response?.data?.error || err.message));
    }
    setUploading(false);
  };

  const send = async () => {
    if (!to) return;
    setSending(true); setResult(null); setError('');
    try {
      if (mode === 'text') {
        const res = await api.post('/messages/send/text', { wa_number_id: activeNumber?.id, to, text });
        setResult(res.data);
        setText('');
      } else {
        const components = [];
        const info = analyzeTemplate(selectedTpl);

        if (info.headerType && info.headerType !== 'TEXT' && headerUrl.trim()) {
          const mediaType = info.headerType.toLowerCase();
          components.push({
            type: 'header',
            parameters: [{ type: mediaType, [mediaType]: { link: headerUrl.trim() } }],
          });
        }

        const filledParams = bodyParams.filter(p => p.value.trim() !== '');
        if (filledParams.length > 0) {
          components.push({
            type: 'body',
            parameters: filledParams.map(p => {
              const param = { type: 'text', text: p.value.trim() };
              if (isNaN(p.key)) param.parameter_name = p.key;
              return param;
            }),
          });
        }

        const res = await api.post('/messages/send/template', {
          wa_number_id: activeNumber?.id,
          to,
          templateName: selectedTpl.name,
          languageCode: langCode,
          components,
        });
        setResult(res.data);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Send failed');
    }
    setSending(false);
  };

  const tplInfo = selectedTpl ? analyzeTemplate(selectedTpl) : null;
  const selectVal = selectedTpl ? `${selectedTpl.name}|${selectedTpl.language}` : '';

  const mediaLabel = tplInfo?.headerType === 'DOCUMENT' ? 'PDF Document'
    : tplInfo?.headerType === 'IMAGE' ? 'Image'
    : tplInfo?.headerType === 'VIDEO' ? 'Video' : '';

  const mediaAccept = tplInfo?.headerType === 'DOCUMENT' ? '.pdf'
    : tplInfo?.headerType === 'IMAGE' ? '.jpg,.jpeg,.png'
    : tplInfo?.headerType === 'VIDEO' ? '.mp4' : '*';

  const windowClosed = windowStatus && !windowStatus.windowOpen;

  return (
    <div style={{ padding: '20px 16px', maxWidth: 560, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 20 }}>
        SEND MESSAGE
      </h2>

      {/* Mode Toggle */}
      <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--r)', padding: 4, marginBottom: 20, border: '1px solid var(--border)' }}>
        {['text', 'template'].map(m => (
          <button key={m} onClick={() => setMode(m)} style={{
            flex: 1, padding: '8px',
            background: mode === m ? 'var(--accent)' : 'transparent',
            color: mode === m ? '#000' : 'var(--text2)',
            fontWeight: mode === m ? 700 : 400,
            borderRadius: 6, border: 'none', fontSize: 13, transition: 'all 0.15s',
          }}>
            {m === 'text' ? '💬 Text' : '📋 Template'}
            {/* Lock hint when window closed */}
            {m === 'text' && windowClosed && (
              <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.7 }}>🔒</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* To */}
        <div>
          <label className="label">To (with country code)</label>
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="919876543210"
            style={{ fontFamily: 'var(--mono)' }} inputMode="numeric" />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>No + sign. E.g. 919876543210</div>
        </div>

        {/* Window status banner — shown whenever 'to' is filled */}
        {(to.length >= 7) && (
          <WindowBanner status={windowStatus} loading={windowLoading} />
        )}

        {mode === 'text' ? (
          <div>
            <label className="label">Message</label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
              placeholder="Type your message..." style={{ resize: 'vertical' }}
              disabled={windowClosed}
            />
            {windowClosed && (
              <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                Window is closed — switch to Template mode above.
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Template select */}
            <div>
              <label className="label">Template</label>
              <select value={selectVal} onChange={e => handleTemplateChange(e.target.value)}>
                <option value="">— Select template —</option>
                {templates.map(t => (
                  <option key={t.id} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>
                ))}
              </select>
              {tplInfo?.bodyText && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--bg3)', borderRadius: 6, fontSize: 12, color: 'var(--text2)', border: '1px solid var(--border)', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                  {tplInfo.bodyText}
                </div>
              )}
            </div>

            <div>
              <label className="label">Language Code</label>
              <input value={langCode} onChange={e => setLangCode(e.target.value)} style={{ maxWidth: 140 }} />
            </div>

            {/* Media header upload */}
            {tplInfo?.headerType && tplInfo.headerType !== 'TEXT' && (
              <div>
                <label className="label">
                  {tplInfo.headerType === 'DOCUMENT' ? '📄' : tplInfo.headerType === 'IMAGE' ? '🖼️' : '🎥'} {mediaLabel}
                  <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6, fontSize: 11 }}>required</span>
                </label>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
                  >
                    {uploading ? '⏳ Uploading...' : `📁 Upload ${mediaLabel}`}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>or paste URL below</span>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept={mediaAccept}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />

                <input
                  value={headerUrl}
                  onChange={e => setHeaderUrl(e.target.value)}
                  placeholder={`https://yourdomain.com/file.pdf`}
                  style={{ marginTop: 8 }}
                />

                {headerUrl && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--accent)', wordBreak: 'break-all' }}>
                    ✅ {headerUrl}
                  </div>
                )}
              </div>
            )}

            {/* Body variables */}
            {tplInfo && tplInfo.bodyParams.length > 0 && (
              <div>
                <label className="label">
                  Body Variables
                  <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6, fontSize: 11 }}>
                    ({tplInfo.hasNamedParams ? 'named' : 'positional'})
                  </span>
                </label>
                {bodyParams.map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12, minWidth: 90, flexShrink: 0 }}>
                      {`{{${p.key}}}`}
                    </span>
                    <input value={p.value} onChange={e => updateParam(i, e.target.value)} placeholder={`Enter ${p.key}`} />
                  </div>
                ))}
              </div>
            )}

            {tplInfo && tplInfo.bodyParams.length === 0 && selectedTpl && (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>ℹ️ No variables — template sends as-is.</div>
            )}
          </>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 14px', color: 'var(--danger)', fontSize: 13 }}>
            ⚠ {error}
          </div>
        )}
        {result && (
          <div style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid var(--accent)', borderRadius: 'var(--r-sm)', padding: '10px 14px', color: 'var(--accent)', fontSize: 13 }}>
            ✅ Sent! ID: {result.result?.messages?.[0]?.id?.slice(0, 30)}...
          </div>
        )}

        <button className="btn-primary" onClick={send}
          disabled={sending || !to || (mode === 'text' && (!text || windowClosed)) || (mode === 'template' && !selectedTpl)}
          style={{ padding: '12px', width: '100%', fontSize: 15 }}>
          {sending ? 'Sending…' : '📤 Send Message'}
        </button>
      </div>
    </div>
  );
}
